import { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import {
  LayoutDashboard, Newspaper, QrCode, User, LogOut, Building2, Wallet, Settings,
  Menu, ChevronLeft, Users as UsersIcon, Map as MapIcon, Home, Globe, GraduationCap,
  Bot, Calendar, LineChart, Handshake, FileText, MessageSquare, ChevronRight, Gift,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { LOGO_DARK_BG } from '../../lib/constants';
import NotificationBell from '../../components/NotificationBell';
import InstallPwa from '../../components/InstallPwa';

const DRAWER_ICONS = {
  Home, Globe, Users: UsersIcon, GraduationCap, Bot, Calendar, LineChart,
  Handshake, FileText, MessageSquare, Wallet, Settings, Gift,
};

const PRIMARY_TABS = [
  { to: '/app', label: 'Inicio', icon: LayoutDashboard, end: true, testid: 'app-nav-dashboard' },
  { to: '/app/feed', label: 'Feed', icon: Newspaper, testid: 'app-nav-feed' },
  { to: '/app/mapa', label: 'Mapa', icon: MapIcon, testid: 'app-nav-map' },
  { to: '/app/propiedades', label: 'MLS', icon: Building2, testid: 'app-nav-properties' },
  { to: '/app/credencial', label: 'Credencial', icon: QrCode, testid: 'app-nav-credential' },
];

const DRAWER_LINKS = [
  { to: '/app/cuotas', label: 'Mis cuotas', icon: Wallet, testid: 'app-drawer-dues' },
  { to: '/app/perfil', label: 'Mi perfil', icon: User, testid: 'app-drawer-profile' },
];

const SCREEN_TITLES = {
  '/app': 'Inicio',
  '/app/feed': 'Feed profesional',
  '/app/mapa': 'Mapa colaborativo',
  '/app/propiedades': 'Mis propiedades',
  '/app/credencial': 'Mi credencial',
  '/app/cuotas': 'Cuotas y vigencia',
  '/app/perfil': 'Mi perfil',
  '/app/beneficios': 'Beneficios',
  '/app/directorio': 'Directorio de socios',
  '/app/eventos': 'Eventos y congresos',
  '/app/chat': 'Chat IA Inmobiliario',
  '/app/campus': 'Campus Virtual',
  '/app/observatorio': 'Observatorio',
  '/app/oportunidades': 'Oportunidades',
  '/app/biblioteca': 'Biblioteca Legal',
  '/app/mensajeria': 'Mensajería',
};

function screenTitle(pathname) {
  if (pathname.startsWith('/app/feed/')) return 'Publicación';
  return SCREEN_TITLES[pathname] || 'CNIB Connect';
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  const title = screenTitle(location.pathname);
  const canGoBack = location.pathname.startsWith('/app/feed/') || location.pathname.split('/').length > 3;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="member-app-layout">
      {/* Sidebar desktop */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col bg-cnib-navy lg:flex">
        <Link to="/" className="flex h-20 items-center px-6">
          <img src={LOGO_DARK_BG} alt="CNIB" className="h-10 w-auto" />
        </Link>
        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
          {(user?.role === 'admin' || user?.role === 'superadmin') && (
            <NavLink
              to="/admin"
              data-testid="app-nav-admin-dashboard"
              className="mb-3 flex items-center gap-3 rounded-xl border border-orange-500/40 bg-orange-500/10 px-4 py-3 text-sm font-bold text-orange-400 transition-colors hover:bg-orange-500 hover:text-white"
            >
              <Settings className="h-5 w-5" />
              Dashboard administración
            </NavLink>
          )}
          {PRIMARY_TABS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`${item.testid}-desktop`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
                  isActive ? 'bg-orange-500 text-white' : 'text-slate-300 hover:bg-white/5 hover:text-white'
                }`
              }
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </NavLink>
          ))}
          <p className="mt-4 px-4 text-[10px] font-bold uppercase tracking-wider text-slate-500">Más módulos</p>
          {[
            { to: '/app/beneficios', label: 'Beneficios', icon: Gift, testid: 'app-nav-benefits-desktop' },
            { to: '/app/directorio', label: 'Directorio', icon: UsersIcon, testid: 'app-nav-directory-desktop' },
            { to: '/app/eventos', label: 'Eventos', icon: Calendar, testid: 'app-nav-events-desktop' },
            { to: '/app/cuotas', label: 'Mis cuotas', icon: Wallet, testid: 'app-nav-dues-desktop' },
            { to: '/app/perfil', label: 'Mi perfil', icon: User, testid: 'app-nav-profile-desktop' },
          ].map((item) => (
            <NavLink
              key={item.testid}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
                  isActive ? 'bg-orange-500 text-white' : 'text-slate-300 hover:bg-white/5 hover:text-white'
                }`
              }
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </NavLink>
          ))}
          {/* Mi micrositio: always visible, opens public microsite in new tab */}
          <a
            href={user?.public_slug ? `/socio/${user.public_slug}` : '/app/perfil'}
            target={user?.public_slug ? '_blank' : undefined}
            rel={user?.public_slug ? 'noopener noreferrer' : undefined}
            data-testid="app-nav-microsite-desktop"
            title={user?.public_slug ? 'Abrir mi micrositio en una nueva pestaña' : 'Completa tu perfil para activar tu micrositio'}
            className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-slate-300 transition-colors hover:bg-white/5 hover:text-white"
          >
            <Globe className="h-5 w-5" />
            Mi micrositio
            <ChevronRight className="ml-auto h-4 w-4 opacity-50" />
          </a>
          <p className="mt-4 px-4 text-[10px] font-bold uppercase tracking-wider text-slate-500">Próximamente</p>
          {[
            { to: '/app/chat', label: 'Chat IA Inmobiliario', icon: Bot },
            { to: '/app/campus', label: 'Campus Virtual', icon: GraduationCap },
            { to: '/app/observatorio', label: 'Observatorio', icon: LineChart },
            { to: '/app/oportunidades', label: 'Oportunidades', icon: Handshake },
            { to: '/app/biblioteca', label: 'Biblioteca Legal', icon: FileText },
            { to: '/app/mensajeria', label: 'Mensajería', icon: MessageSquare },
          ].map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
                  isActive ? 'bg-orange-500 text-white' : 'text-slate-400 hover:bg-white/5 hover:text-white'
                }`
              }
            >
              <item.icon className="h-5 w-5" />
              {item.label}
              <span className="ml-auto rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-400">Pronto</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-4">
          <div className="mb-3 px-2">
            <p className="truncate text-sm font-bold text-white">{user?.name}</p>
            <p className="truncate text-xs text-slate-400">{user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="app-logout-button"
            className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-300 transition-colors hover:bg-white/5 hover:text-orange-400"
          >
            <LogOut className="h-4 w-4" /> Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Mobile top app bar (app-shell style) */}
      <header
        className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-slate-200 bg-white px-3 lg:hidden"
        data-testid="app-topbar"
      >
        {canGoBack ? (
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex h-10 w-10 items-center justify-center rounded-full text-slate-600 hover:bg-slate-100"
            data-testid="app-topbar-back"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="flex h-10 w-10 items-center justify-center rounded-full text-slate-600 hover:bg-slate-100"
            data-testid="app-topbar-menu"
          >
            <Menu className="h-6 w-6" />
          </button>
        )}
        <p className="font-heading text-base font-extrabold text-slate-900">{title}</p>
        <div className="flex items-center gap-1">
          <NotificationBell />
          <NavLink to="/app/perfil" data-testid="app-topbar-profile" className="ml-1">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-9 w-9 rounded-full object-cover ring-2 ring-orange-400" />
            ) : (
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-cnib-blue text-sm font-extrabold text-white ring-2 ring-orange-400">
                {user?.name?.charAt(0) || 'S'}
              </span>
            )}
          </NavLink>
        </div>
      </header>

      {/* Desktop header */}
      <header className="sticky top-0 z-30 hidden h-16 items-center justify-between border-b border-slate-200 bg-white px-4 lg:ml-64 lg:flex lg:px-8">
        <p className="font-heading text-lg font-bold text-slate-900">{title}</p>
        <div className="flex items-center gap-3">
          <InstallPwa />
          <NotificationBell />
          <span className="hidden text-sm text-slate-500 sm:block">{user?.name}</span>
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-cnib-blue font-heading text-sm font-bold text-white">
            {user?.name?.charAt(0) || 'S'}
          </span>
        </div>
      </header>

      <main className="px-3 pb-24 pt-4 lg:ml-64 lg:px-8 lg:pb-10 lg:pt-6">
        <Outlet />
      </main>

      {/* Mobile bottom tab bar */}
      <nav
        className="fixed inset-x-0 bottom-0 z-40 flex items-stretch justify-around border-t border-slate-200 bg-white pb-[env(safe-area-inset-bottom)] lg:hidden"
        data-testid="app-bottom-nav"
      >
        {PRIMARY_TABS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={item.testid}
            className={({ isActive }) =>
              `relative flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-bold transition-colors ${
                isActive ? 'text-orange-500' : 'text-slate-400'
              }`
            }
          >
            {({ isActive }) => (
              <>
                {isActive && <span className="absolute top-0 h-0.5 w-10 rounded-full bg-orange-500" />}
                <item.icon className={`h-5 w-5 ${isActive ? 'scale-110' : ''} transition-transform`} />
                <span>{item.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Drawer mobile — estilo Módulos */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-50 flex lg:hidden"
          onClick={() => setDrawerOpen(false)}
          data-testid="app-drawer"
        >
          <div className="h-full w-80 overflow-y-auto bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Header user card */}
            <div className="bg-cnib-navy p-5">
              <div className="flex items-center gap-3">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="h-12 w-12 rounded-full object-cover ring-2 ring-orange-500" />
                ) : (
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-500 text-base font-extrabold text-white">
                    {user?.name?.charAt(0) || 'S'}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-extrabold text-white">{user?.name}</p>
                  <p className="truncate text-xs text-slate-400">{user?.email}</p>
                  {user?.member_number && (
                    <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-orange-400">{user.member_number}</p>
                  )}
                </div>
              </div>
            </div>

            {(user?.role === 'admin' || user?.role === 'superadmin') && (
              <Link
                to="/admin"
                onClick={() => setDrawerOpen(false)}
                data-testid="app-drawer-admin"
                className="mx-4 mt-4 flex items-center gap-3 rounded-xl border border-orange-500/40 bg-orange-500/10 px-4 py-3 text-sm font-bold text-orange-500"
              >
                <Settings className="h-5 w-5" />
                Panel administración
              </Link>
            )}

            <p className="mt-5 px-5 text-center text-sm font-bold text-slate-900">Módulos</p>
            <div className="grid grid-cols-1 gap-1 p-4" data-testid="app-drawer-modules">
              {/* --- Módulos activos --- */}
              {[
                { to: '/app/propiedades', title: 'Mis Propiedades', desc: 'Administra tus inmuebles', icon: 'Home', color: 'bg-orange-500' },
                { to: user?.public_slug ? `/socio/${user.public_slug}` : null, title: 'Mi Micrositio', desc: 'Ver mi sitio público', icon: 'Globe', color: 'bg-sky-500', external: true, disabled: !user?.public_slug },
                { to: '/app/directorio', title: 'Directorio de Socios', desc: 'Busca y conecta con socios', icon: 'Users', color: 'bg-violet-500' },
                { to: '/app/beneficios', title: 'Beneficios', desc: 'Descuentos y convenios', icon: 'Gift', color: 'bg-amber-500' },
                { to: '/app/eventos', title: 'Eventos', desc: 'Congresos y capacitaciones', icon: 'Calendar', color: 'bg-indigo-500' },
                { to: '/app/cuotas', title: 'Mis Cuotas', desc: 'Estado de membresía', icon: 'Wallet', color: 'bg-cyan-500' },
                { to: '/app/perfil', title: 'Configuración', desc: 'Ajustes de tu cuenta', icon: 'Settings', color: 'bg-slate-500' },
              ].map((m, idx) => {
                const Icon = DRAWER_ICONS[m.icon] || UsersIcon;
                const Content = (
                  <div className={`flex items-center gap-3 rounded-2xl p-3 transition-colors hover:bg-slate-50 ${m.disabled ? 'opacity-50' : ''}`}>
                    <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${m.color} text-white shadow-sm`}>
                      <Icon className="h-5 w-5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-extrabold text-slate-900">{m.title}</p>
                      <p className="truncate text-xs text-slate-500">{m.desc}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-300" />
                  </div>
                );
                if (m.disabled || !m.to) {
                  return (
                    <button
                      key={`active-${idx}`}
                      type="button"
                      onClick={() => setDrawerOpen(false)}
                      data-testid={`drawer-module-${idx}`}
                      className="w-full text-left"
                    >
                      {Content}
                    </button>
                  );
                }
                if (m.external) {
                  return (
                    <a
                      key={`active-${idx}`}
                      href={m.to}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setDrawerOpen(false)}
                      data-testid={`drawer-module-${idx}`}
                    >
                      {Content}
                    </a>
                  );
                }
                return (
                  <Link
                    key={`active-${idx}`}
                    to={m.to}
                    onClick={() => setDrawerOpen(false)}
                    data-testid={`drawer-module-${idx}`}
                  >
                    {Content}
                  </Link>
                );
              })}

              {/* --- Próximamente --- */}
              <p className="mt-5 mb-1 px-3 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Próximamente</p>
              {[
                { to: '/app/chat', title: 'Chat IA Inmobiliario', desc: 'Asesoría legal y normativa', icon: 'Bot', color: 'bg-fuchsia-500' },
                { to: '/app/campus', title: 'Campus Virtual', desc: 'Cursos y certificaciones', icon: 'GraduationCap', color: 'bg-teal-500' },
                { to: '/app/observatorio', title: 'Observatorio Inmobiliario', desc: 'Indicadores y reportes', icon: 'LineChart', color: 'bg-emerald-500' },
                { to: '/app/oportunidades', title: 'Oportunidades', desc: 'Bolsa de negocios', icon: 'Handshake', color: 'bg-amber-600' },
                { to: '/app/biblioteca', title: 'Biblioteca Legal', desc: 'Contratos y documentos', icon: 'FileText', color: 'bg-slate-700' },
                { to: '/app/mensajeria', title: 'Mensajería', desc: 'Chat entre socios', icon: 'MessageSquare', color: 'bg-rose-500' },
              ].map((m, idx) => {
                const Icon = DRAWER_ICONS[m.icon] || UsersIcon;
                return (
                  <Link
                    key={`soon-${idx}`}
                    to={m.to}
                    onClick={() => setDrawerOpen(false)}
                    data-testid={`drawer-module-soon-${idx}`}
                    className="opacity-80"
                  >
                    <div className="flex items-center gap-3 rounded-2xl p-3 transition-colors hover:bg-slate-50">
                      <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${m.color} text-white shadow-sm`}>
                        <Icon className="h-5 w-5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="flex items-center gap-1.5 text-sm font-extrabold text-slate-900">
                          {m.title}
                          <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-700">Pronto</span>
                        </p>
                        <p className="truncate text-xs text-slate-500">{m.desc}</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-300" />
                    </div>
                  </Link>
                );
              })}
            </div>

            <div className="border-t border-slate-100 p-4">
              <InstallPwa />
              <button
                onClick={handleLogout}
                data-testid="app-drawer-logout"
                className="mt-2 flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-500 hover:bg-slate-50 hover:text-red-500"
              >
                <LogOut className="h-4 w-4" /> Cerrar sesión
              </button>
            </div>
          </div>
          <div className="flex-1" />
        </div>
      )}
    </div>
  );
}
